import { memo, useEffect, useMemo } from "react";
import { View } from "react-native";
import Animated, {
	useAnimatedStyle,
	useSharedValue,
	withSpring,
	withTiming,
} from "react-native-reanimated";
import usePalette from "src/hooks/usePalette";
import { MultiStageToggleProps } from "./types";

const MultiStageToggle = ({
	Components,
	selectedIndex = 0,
	selectBubbleStyle,
	animated = true,
	bubbleWidth,
}: MultiStageToggleProps) => {
	const numItems = useMemo(() => Components?.filter(Boolean)?.length ?? 1, [Components]);
	const xPctSV = useSharedValue((selectedIndex + 0.5) / numItems - 0.5);
	const widthSV = useSharedValue(0);
	useEffect(() => {
		// Negative shift because the bubble is centered
		const value = (selectedIndex + 0.5) / numItems - 0.5;
		xPctSV.value = animated ? withSpring(value, { damping: 24, stiffness: 180 }) : value;
	}, [animated, numItems, selectedIndex, xPctSV]);

	const animatedStyle = useAnimatedStyle(
		() => ({
			// Using transform for performance
			transform: [
				{
					translateX: xPctSV.value * widthSV.value,
				},
			],
			opacity: withTiming(widthSV.value > 0 ? 1 : 0, {
				duration: 200,
			}),
		}),
		[widthSV, xPctSV],
	);
	const { primary, isFallback } = usePalette();

	return (
		<>
			<View
				style={{
					flexDirection: "row",
					zIndex: 2,
				}}
				onLayout={(event) => {
					widthSV.value = event.nativeEvent.layout.width;
				}}
			>
				{Components}
			</View>
			<Animated.View
				style={[
					{
						position: "absolute",
						width: bubbleWidth ?? `${(1 / numItems) * 100}%`,
						height: "100%",
						zIndex: 1,
						alignItems: "center",
						justifyContent: "center",
						alignSelf: "center",
					},
					animatedStyle,
				]}
			>
				<View
					style={[
						{
							backgroundColor: isFallback ? "#D9D9D94D" : `${primary}99`,
							width: "100%",
							height: "100%",
						},
						selectBubbleStyle,
						{
							position: "absolute",
						},
					]}
				/>
			</Animated.View>
		</>
	);
};
export default memo(MultiStageToggle);
